import { TypeKeys } from '.';
import { swApi } from '../global/app';
import { Capacitor } from '@capacitor/core';
import { updateWorkoutSettings } from './workout-player';
import { hideGlobalLoading, showGlobalLoading } from './deploy-update';
import { showGlobalModal } from './global-modal';
import { ModalOptions } from '@ionic/core';
import { presentToast } from '../components/shared/sw-favorited-toast/show-favorited-toast';
import { amplitudeLogEvent, AmplitudeEvents } from '../global/amplitude';
import {
  Account,
  FavoriteWorkoutsDoc,
  Integrations,
  LanguageString,
  User,
  UserStats,
  WorkoutSettings,
} from '@sworkit/api';
import {
  updateAccount,
  getUserDocListener,
  updateIntegrations,
  saveWorkoutSettings,
  updateProfile,
  getProfileWithWeight,
} from '../global/firestore-user';
import {
  firestoreFavoritesListener,
  updateFavorite,
} from '../global/firestore-favorites';
import { getStats } from '../global/firestore-user-stats';
import {
  getWellhubIdFromUserDoc,
  logWellhubAnalytics,
} from '../global/wellhub';
import { checkIfEmailVerifiedAndRequired } from './email-status-requirement';
import { updateUserReleaseData } from '../global/set-new-release-to-badge';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { objectsDeeplyEqual } from '../components/sw-app/sw-portal/portal-utils';
import swWorkoutSettings from '../stencil-store/sw-workout-settings.store';
export interface GetUserStats {
  type: TypeKeys.GET_USER_STATS;
}
export interface SetUserStats {
  type: TypeKeys.SET_USER_STATS;
  userStats: UserStats;
}
export const getUserStats = () => async (dispatch) => {
  const action: GetUserStats = {
    type: TypeKeys.GET_USER_STATS,
  };
  dispatch(action);
  const userStats = await getStats();
  if (!userStats) return;
  const setAction: SetUserStats = {
    type: TypeKeys.SET_USER_STATS,
    userStats,
  };
  dispatch(setAction);
};
let firstLoad = true;
let userDocListener;

export const listenToUserDoc =
  (subscribe: boolean) => async (dispatch, _getState: () => SworkitState) => {
    if (subscribe && !userDocListener) {
      // only subscribe if they aren't already subscribed
      userDocListener = getUserDocListener((userDoc) => {
        const oldWorkoutSettings = { ...swWorkoutSettings.workoutSettings };
        dispatch(setUserDoc(userDoc));
        dispatch(checkIfEmailVerifiedAndRequired());
        swWorkoutSettings.workoutSettings = { ...userDoc.workoutSettings };
        updateUserReleaseData(userDoc?.meta?.releaseData);
        if (firstLoad) {
          //log gym pass sign in event for first load
          const wellhubId = getWellhubIdFromUserDoc(userDoc);
          logWellhubAnalytics(wellhubId, 'signin');
        }
        swApi?.changeEnvironment(userDoc?.account?.env);
        if (userDoc?.workoutSettings) {
          // loading initial settings for user
          const workoutSettings = { ...userDoc.workoutSettings };
          const { weightInLbs: newWeightInLbs } =
            getProfileWithWeight(userDoc?.profile) || {};

          if (
            typeof newWeightInLbs !== 'undefined' &&
            swWorkoutSettings.workoutSettings.userWeightInLbs != newWeightInLbs
          ) {
            workoutSettings.userWeightInLbs = newWeightInLbs;
          }

          // console.log({oldWorkoutSettings}, {workoutSettings})
          if (!objectsDeeplyEqual(oldWorkoutSettings, workoutSettings)) {
            updateWorkoutSettings({ ...workoutSettings });
          }
        }

        firstLoad = false;
      });
      console.debug('subscribe to user doc listener');
    } else if (!subscribe && userDocListener) {
      userDocListener();
      userDocListener = null;
      console.debug('unsubscribe from user doc listener');
    }
  };

export interface SetUserDoc {
  type: TypeKeys.SET_USER_DOC;
  userDoc: Partial<User>;
}

const setUserDoc = (userDoc: Partial<User>) => {
  const action: SetUserDoc = {
    type: TypeKeys.SET_USER_DOC,
    userDoc,
  };
  return action;
};

export const checkIfMilestoneCompleted =
  (
    sessionsBefore: User['allTimeStats']['totalSessions'],
    sessionsAfter: User['allTimeStats']['totalSessions'],
  ) =>
  (dispatch, getState) => {
    // watching the totalsession count in sw-app.tsx
    // allTimeStats can take some time to update so we locally increase totalSessions on the end workout screen, we also locally increase totalSessions when creating an external activity log: this way we can show the modal instantly
    const {
      ui: { globalModalShown },
    } = getState() as SworkitState;

    const milestone = isMilestone(sessionsAfter);
    if (
      !milestone ||
      globalModalShown ||
      !sessionsAfter ||
      !sessionsBefore ||
      sessionsAfter === sessionsBefore ||
      sessionsAfter < sessionsBefore
    ) {
      // only show if the session count increased
      return;
    }

    const options: ModalOptions = {
      component: 'sw-milestone-modal',
      cssClass: 'sw-small-modal',
      componentProps: {
        workoutsCompleted: sessionsAfter,
      },
    };
    return dispatch(showGlobalModal(options));
  };

function isMilestone(sessionCount: number) {
  if (!sessionCount) return false;
  const milestones: number[] = [
    3, 10, 20, 30, 40, 50, 75, 100, 125, 150, 175, 200,
  ];
  const milestone = milestones.indexOf(sessionCount) != -1;
  const fifties = sessionCount % 50 === 0;
  return milestone || fifties;
}
export interface SetUserAccount {
  type: TypeKeys.SET_USER_ACCOUNT;
  account: Partial<Account>;
}

export const updateUserAccount =
  (account: Partial<Account>) => async (dispatch) => {
    const action: SetUserAccount = {
      type: TypeKeys.SET_USER_ACCOUNT,
      account,
    };
    dispatch(action);
    return updateAccount(account);
  };
export interface SetUserIntegrations {
  type: TypeKeys.SET_USER_INTEGRATIONS;
  integrations: Partial<Integrations>;
}

export const updateUserIntegrations =
  (integrations: Partial<Integrations>) => async (dispatch) => {
    if (integrations) {
      updateIntegrations(integrations);
    }
    const action: SetUserIntegrations = {
      type: TypeKeys.SET_USER_INTEGRATIONS,
      integrations,
    };
    dispatch(action);
  };

export interface SetUserLocale {
  type: TypeKeys.SET_USER_LOCALE;
  locale: LanguageString;
}

export const setUserLocale = (locale: LanguageString) => async (dispatch) => {
  await showGlobalLoading({});
  const action: SetUserLocale = {
    type: TypeKeys.SET_USER_LOCALE,
    locale,
  };
  dispatch(action);
  await dispatch(updateUserAccount({ locale }));
  if (Capacitor.isNativePlatform()) {
    try {
      await Filesystem.rmdir({
        path: '/voices',
        directory: Directory.Data,
        recursive: true,
      });
    } catch (err) {
      console.debug('err removing dir', err);
    }
  }
  await swApi.changeLanguage(locale);
  hideGlobalLoading();
  location.reload();
};

export const uploadUserProfilePhoto = (photo: string) => async (dispatch) => {
  dispatch(userProfilePhotoUploading(photo));
  await updateProfile({ photo });
  dispatch(userProfilePhotoUploaded());
};

export interface UserProfilePhotoUploading {
  type: TypeKeys.USER_PROFILE_PHOTO_UPLOADING;
  photo: string;
}

function userProfilePhotoUploading(photo: string) {
  const action: UserProfilePhotoUploading = {
    type: TypeKeys.USER_PROFILE_PHOTO_UPLOADING,
    photo,
  };
  return action;
}

export interface UserProfilePhotoUploaded {
  type: TypeKeys.USER_PROFILE_PHOTO_UPLOADED;
}

function userProfilePhotoUploaded() {
  const action: UserProfilePhotoUploaded = {
    type: TypeKeys.USER_PROFILE_PHOTO_UPLOADED,
  };
  return action;
}

export const updateUserWorkoutSettings =
  (workoutSettings: Partial<WorkoutSettings>) => async (_dispatch) => {
    if (!workoutSettings) return;
    saveWorkoutSettings(workoutSettings); //saves to firestore which has a trigger to update state
  };

export interface IncreaseSessionCount {
  type: TypeKeys.INCREASE_SESSION_COUNT;
}

export const increaseSessionCount = () => (dispatch, _getState) => {
  const action: IncreaseSessionCount = {
    type: TypeKeys.INCREASE_SESSION_COUNT,
  };
  return dispatch(action);
};

let favoritesListener;
export const listenToFavorites =
  (subscribe: boolean) => (dispatch, _getState) => {
    if (subscribe && !favoritesListener) {
      favoritesListener = firestoreFavoritesListener((workoutSlugs) =>
        dispatch(setFavorites(workoutSlugs)),
      );
    } else if (!subscribe && favoritesListener) {
      favoritesListener();
      favoritesListener = null;
    }
  };

export interface SetFavorites {
  type: TypeKeys.SET_FAVORITES;
  favorites: FavoriteWorkoutsDoc['workouts'];
}

const setFavorites = (favorites: FavoriteWorkoutsDoc['workouts']) => {
  const action: SetFavorites = {
    type: TypeKeys.SET_FAVORITES,
    favorites,
  };
  return action;
};

export const toggleFavorite =
  (
    location:
      | 'workout-page'
      | 'end-workout-page'
      | 'suggested-favorites'
      | 'meditation-page'
      | 'nutrition-page',
    workoutSlug: string,
    showToast?: boolean,
  ) =>
  (_dispatch, getState) => {
    const {
      user: { favorites },
    } = getState() as SworkitState;
    const favorite = favorites.find(
      (favorite) => favorite.slug === workoutSlug,
    );
    if (favorite) {
      //already there, remove it
      updateFavorite(favorite, false);
      return;
    }
    updateFavorite({ slug: workoutSlug }, true);
    amplitudeLogEvent(AmplitudeEvents.added_to_favorites, {
      location,
      workout_slug: workoutSlug,
    });
    if (!showToast) return;
    // showFavoritedToast();
    presentToast();
  };
